import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api, type ApiError } from '@/lib/api';
import {
  afterRequestChanges,
  afterRunAccepted,
  afterRunSettled,
  qk,
} from '@/lib/query-keys';
import type {
  AnswerAskRequest,
  ChatStatus,
  ClientChatStatus,
  ExpertChat,
  PostMessageRequest,
} from '@/types/api';

/* Types are generated from the contract; hooks are hand-written, because the
   cross-entity invalidation in lib/query-keys.ts is the part a generator cannot
   know: posting a message starts a run, a run in flight closes the composer and
   blocks a promotion, and once it pushes the diff is stale. */

export interface RunAccepted {
  run_id: number;
  message_seq: number;
  stream_url: string;
  stream_cursor?: string | null;
  status: ChatStatus;
  client_visible_status?: ClientChatStatus;
  idempotent_replay?: boolean;
}

export function useExpertChat(chatId: number, enabled = true): UseQueryResult<ExpertChat, ApiError> {
  return useQuery({
    queryKey: qk.chat(chatId),
    queryFn: () => api.get<ExpertChat>(`/expert/chats/${chatId}`, { limit: 200 }),
    enabled,
    // The stream carries the events; this document carries the predicates. It is
    // re-fetched on the stream's say-so (`refetchToken`) rather than on a timer,
    // with a slow safety net for the case where a frame was missed entirely.
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Prompt the agent in the review thread.
 *
 * The message is inserted at `visibility: expert` by the single server-side
 * `InsertMessage` constructor — there is no other insert path, which is why I7
 * cannot be broken by forgetting a filter here. `client_message_id` makes a
 * retry idempotent: a double-tap on a slow connection returns the original 202
 * rather than starting a second run on the project.
 */
export function usePostExpertMessage(chatId: number) {
  const qc = useQueryClient();
  return useMutation<RunAccepted, ApiError, PostMessageRequest>({
    mutationFn: (body) => api.post<RunAccepted>(`/expert/chats/${chatId}/messages`, body),
    onSettled: () => afterRunAccepted(qc, chatId),
  });
}

/**
 * Answer a parked ask.
 *
 * A denial is delivered WITH its explanatory text rather than as a kill: the
 * agent then finishes coherently, where an interrupt leaves a half-applied tree.
 */
export function useAnswerAsk(chatId: number) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { askId: string; body: AnswerAskRequest }>({
    mutationFn: ({ askId, body }) =>
      api.post(`/expert/chats/${chatId}/answers/${encodeURIComponent(askId)}`, body),
    onSettled: () => afterRunSettled(qc, chatId),
  });
}

/**
 * Hand the thread back to the client.
 *
 * The one message an expert writes that the client reads. It moves the chat to
 * `changes requested`, UNFREEZES the project and releases the claim — so it
 * leaves the queue entirely, which is why the invalidation reaches the projects
 * list as well as the queue.
 */
export function useRequestChanges(chatId: number) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { message: string }>({
    mutationFn: (body) => api.post(`/expert/chats/${chatId}/request-changes`, body),
    onSettled: () => afterRequestChanges(qc, chatId),
  });
}
